import React from "react";
import { atom, useRecoilState } from "recoil";
import { useLocation } from "react-router";
import { GKLayoutShell } from "@granular/gds-ux-components";
import { FabricMapProvider } from "@granular/fabric-mapbox-tools";

import {
  useLocale,
  useInvalidateUser,
  useInvalidateOperations,
  useInvalidateFarms,
  useInvalidateFields,
  useInvalidateCrops,
  useInvalidateEnrollments,
  useInvalidateFieldEnrollments,
  useInvalidateFieldCrops,
  useInvalidateMembers,
  useInvalidateInvites,
  useCurrentOperationId,
  useFeatureFlag,
} from "../next/hooks";
import { getInsightsDataDirtyFlag } from "../services/insights";
import { useNav } from "../hooks/useNav";
import ErrorBoundary from "./ErrorBoundary";
import Loading from "./Loading";
import TermsOfService from "./TermsOfService";

import {
  OperationDropdown,
  usePrimaryNavItems,
  useSecondaryNavItems,
} from "./Nav.helpers";
import { NAV_PORTAL_ROOT_ID } from "../constants";
import { ShellProps, NavProps } from "./FabricShell";
import isFunction from "lodash/isFunction";

const fetchOverlayState = atom<boolean>({
  key: "fetchOverlayState",
  default: false,
});

const Component: React.FC<ShellProps> = (props) => {
  const { navProps, ...passthroughProps } = props;
  const locale = useLocale();
  const currentPath = useLocation().pathname;
  const previousPath = React.useRef(currentPath);
  const [fetchOverlay, setFetchOverlay] = useRecoilState(fetchOverlayState);
  const showNav = useNav();
  const navItems = usePrimaryNavItems();
  const navSecondaryItems = useSecondaryNavItems();
  const invalidateUser = useInvalidateUser();
  const invalidateOperations = useInvalidateOperations();
  const invalidateFarms = useInvalidateFarms();
  const invalidateFields = useInvalidateFields();
  const invalidateCrops = useInvalidateCrops();
  const invalidateEnrollments = useInvalidateEnrollments();
  const invalidateFieldEnrollments = useInvalidateFieldEnrollments();
  const invalidateFieldCrops = useInvalidateFieldCrops();
  const invalidateMembers = useInvalidateMembers();
  const invalidateInvites = useInvalidateInvites();
  const currentOperationId = useCurrentOperationId();

  const disableInsightsUIHardRefreshes = useFeatureFlag({
    app: "g_insights",
    key: "disable-forced-insights-ui-hard-refreshes",
  });

  const defaultNavProps: NavProps = showNav
    ? {
        navItems,
        navContent: (
          <>
            <OperationDropdown translateRoutes={props.translateRoutes} />
            <div id={NAV_PORTAL_ROOT_ID} />
          </>
        ),
        navSecondaryItems,
        navHome: { to: "/" },
      }
    : {};

  // This useEffect is to keep Insights in sync with the rest of the environment (and vice versa)
  // This can be safely removed once Insights is using Core Data instead of its own data fetching, mutating, and invalidating.
  React.useEffect(() => {
    const areWeComingFromInsights =
      previousPath.current.startsWith("/app") &&
      !currentPath.startsWith("/app");
    const areWeOnInsights = currentPath.startsWith("/app");

    if (disableInsightsUIHardRefreshes) {
      previousPath.current = currentPath;
      return;
    }

    if (areWeOnInsights && getInsightsDataDirtyFlag()) {
      setFetchOverlay(true);
      window.location.reload();
    } else if (areWeComingFromInsights) {
      setFetchOverlay(true);
      Promise.all([
        invalidateUser(),
        invalidateOperations(),
        invalidateFarms(),
        invalidateFields(),
        invalidateCrops(),
        invalidateEnrollments(),
        invalidateFieldEnrollments(),
        invalidateFieldCrops(),
        invalidateMembers(),
        invalidateInvites(),
      ]).then(() => {
        setFetchOverlay(false);
        previousPath.current = currentPath;
      });
    } else {
      previousPath.current = currentPath;
    }
  }, [
    currentPath,
    invalidateOperations,
    invalidateUser,
    invalidateFarms,
    invalidateFields,
    invalidateCrops,
    invalidateEnrollments,
    invalidateFieldEnrollments,
    invalidateFieldCrops,
    invalidateMembers,
    invalidateInvites,
    setFetchOverlay,
    disableInsightsUIHardRefreshes,
  ]);

  // When changing the currentOperationId, it's good to briefly show a loading indicator because:
  // - Some apps do not handle a change in operation well (like Settings) and will otherwise have bugs or errors.
  // - Some apps are very slow to handle the operation change (like Activities) and this helps give the user some idea as to what is going on.
  React.useEffect(() => {
    setFetchOverlay(true);
    setTimeout(() => {
      setFetchOverlay(false);
    }, 0);
  }, [setFetchOverlay, currentOperationId]);

  const finalNavProps: NavProps = {
    ...defaultNavProps,
    ...(isFunction(navProps) ? navProps() : navProps),
  };

  return (
    <GKLayoutShell alertsOverlay={true} id="fabric-shell" {...finalNavProps}>
      <TermsOfService />
      <FabricMapProvider provideAt="fabric-shell" locale={locale}>
        {fetchOverlay ? (
          <Loading data-qa="gk-loading-app-shell" useGKContent={true} />
        ) : (
          <ErrorBoundary {...passthroughProps} />
        )}
      </FabricMapProvider>
    </GKLayoutShell>
  );
};

export default Component;
